import React, {useCallback, useMemo, useState} from 'react';

import {addToast, Button, Dropdown, DropdownItem, Icon, Text} from '@albert-io/atomic';

import {useShallow} from 'zustand/react/shallow';

import questionEditorStore from '../QuestionEditor/QuestionEditor.store';
import {useQuestionEditorV2Store} from '../QuestionEditorV2Store';

import {PRIMARY_CONTENT_LANGUAGE} from '../QuestionEditorV2Store.types';

import {RegenerateTranslationsModal} from './RegenerateTranslationsModal';

export const GenerateTranslationsButton = () => {
  const {
    generateTranslations,
    generatingTranslations,
    meetsTranslationRequirements,
    currentLanguage,
    translationsHaveChanges,
    hasPublishedTranslatedFields
  } = useQuestionEditorV2Store(
    useShallow((state) => ({
      generateTranslations: state.generateTranslations,
      generatingTranslations: state.generatingTranslations,
      meetsTranslationRequirements:
        !!state.currentTranslatedQuestion()?.meets_translation_requirements,
      hasPublishedTranslatedFields: state.hasPublishedTranslatedFields(),
      currentLanguage: state.currentLanguage,
      translationsHaveChanges: state.hasChanges()
    }))
  );
  const question = questionEditorStore.getQuestion();
  const questionSet = questionEditorStore.getQuestionSet();

  const canGenerateTranslations = useMemo(() => {
    if (!question || !questionSet) {
      return false;
    }

    const isNew = !question.existsOnServer();
    const questionHasChanges =
      !questionSet.getChangeMap().isEmpty() ||
      !question.getChangeMap().isEmpty() ||
      translationsHaveChanges;

    return (
      !isNew &&
      !questionHasChanges &&
      (question?.isTranslate() || question?.isTranslateSupplements())
    );
  }, [question, questionSet, translationsHaveChanges]);

  const doGenerateTranslations = useCallback(
    async (type: 'full' | 'partial') => {
      if (currentLanguage === PRIMARY_CONTENT_LANGUAGE) {
        return;
      }
      try {
        await generateTranslations(currentLanguage, type);
        addToast({
          color: 'positive',
          message: 'Translations generated successfully',
          duration: 4000
        });
      } catch (e) {
        addToast({
          message: 'There was an error generating translations. Please try again.',
          title: 'Error',
          color: 'negative'
        });
      }
    },
    [generateTranslations, currentLanguage]
  );

  const [showConfirmRegenerateModal, setShowConfirmRegenerateModal] = useState(false);

  if (currentLanguage === PRIMARY_CONTENT_LANGUAGE) {
    return null;
  }

  return (
    <>
      {hasPublishedTranslatedFields ? (
        // if there are existing translations, the user can choose full or partial
        <Dropdown
          trigger={
            <Button disabled={!canGenerateTranslations || generatingTranslations}>
              Generate Translations
              {generatingTranslations && <Icon icon='refresh' spin className='u-mar-l_1' />}
              {!generatingTranslations && <Icon icon='caret-down' className='u-mar-l_1' />}
            </Button>
          }
          closeOnItemClicked
        >
          <DropdownItem onClick={() => setShowConfirmRegenerateModal(true)} bold={false}>
            <div className='u-display_flex u-flex-direction_column'>
              <Text size='m'>Full Translation</Text>
              <Text size='xs' color='secondary'>
                Translate all fields, including published fields.
              </Text>
            </div>
          </DropdownItem>
          <DropdownItem
            disabled={meetsTranslationRequirements}
            onClick={() => doGenerateTranslations('partial')}
            bold={false}
          >
            <div className='u-display_flex u-flex-direction_column'>
              <Text size='m'>Partial Translation</Text>
              <Text size='xs' color='secondary'>
                Translate fields, excluding published fields.
              </Text>
            </div>
          </DropdownItem>
        </Dropdown>
      ) : (
        // if there are 0 existing translations, just a regular button works
        <Button
          onClick={() => {
            doGenerateTranslations('full');
          }}
          disabled={!canGenerateTranslations || generatingTranslations}
        >
          <span>Generate Translations</span>
          {generatingTranslations && <Icon icon='refresh' spin className='u-mar-l_1' />}
        </Button>
      )}

      {showConfirmRegenerateModal && (
        <RegenerateTranslationsModal
          type='question'
          doGenerateTranslations={() => doGenerateTranslations('full')}
          setShowConfirmRegenerateModal={setShowConfirmRegenerateModal}
        />
      )}
    </>
  );
};
